import { SharePointListClient, type ListItem } from '../SharePointListClient';

/**
 * 13.Campanias — discount campaigns. Dates are stored as 'dd/mm/yyyy' text
 * and Descuento_C as a decimal-with-comma string ("0,05" = 5%). The legacy
 * app also stamps audit timestamps (FechaCreacion_C, MesCreacion_C, etc.)
 * so we mirror those on create.
 */
export type CampaniasFields = {
  Title?: string | null;
  NombreCampania_C?: string | null;
  ArticuloID_C?: string | null;
  Articulo_C?: string | null;
  Descuento_C?: string | null;
  Desde_C?: string | null;
  Hasta_C?: string | null;
  Status_C?: 'Activo' | 'Finalizado' | 'Inactivo' | string | null;
  FechaCreacion_C?: string | null;
  MesCreacion_C?: string | null;
  AnioCreacion_C?: string | null;
  MesAnioCreacion_C?: string | null;
  HoraCreacion_C?: string | null;
};

export type CampaniaItem = ListItem<CampaniasFields>;

export const campaniasClient = new SharePointListClient<CampaniasFields>('Campanias');

export async function listAllCampanias(): Promise<CampaniaItem[]> {
  return campaniasClient.listItems({ top: 5000 });
}

export async function listActiveCampanias(): Promise<CampaniaItem[]> {
  // `NombreCampania_C` isn't indexed in SharePoint so Graph rejects an
  // orderBy on it. Fetch filtered by Status_C (indexed) and sort in-
  // memory instead — the list is small enough that this is fine.
  const items = await campaniasClient.listItems({
    filter: "fields/Status_C eq 'Activo'",
  });
  return items.sort((a, b) =>
    (a.fields.NombreCampania_C ?? '').localeCompare(b.fields.NombreCampania_C ?? ''),
  );
}

// --------------------------------------------------------------------------
// Date + number helpers
// --------------------------------------------------------------------------

// Pure date/status logic lives in `campaniasLogic` (no SP-client deps) so
// it can be unit-tested. Re-exported here so existing import sites
// (`from '.../campanias'`) keep working unchanged.
import { isoToAr, pad2 } from './campaniasLogic';
export {
  arToIso,
  campaniaDisplayStatus,
  isCampaniaVigente,
  isoToAr,
} from './campaniasLogic';

/** e.g. 5 (%) → '0,05'. Keeps 4 decimals max. */
function percentToStoredDecimal(pct: number): string {
  return String(Math.round((pct / 100) * 10_000) / 10_000).replace('.', ',');
}

/** Stored decimal ('0,05') back to the percentage the admin entered (5). */
export function storedDecimalToPercent(stored: string | number | null | undefined): number | null {
  if (stored == null || stored === '') return null;
  const s = typeof stored === 'number' ? String(stored) : stored.replace(',', '.');
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 10_000) / 100; // up to 2 decimal places
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

export type CampaniaPayload = {
  nombre: string;
  /** SharePoint list item id of the selected articulo. */
  articuloId: string;
  /** Display value of the selected articulo (Articulo_ART). */
  articuloNombre: string;
  /** Percentage as the user typed it (e.g. 5 for 5 %). */
  descuentoPct: number;
  /** 'yyyy-mm-dd' (from native date inputs). */
  desde: string;
  /** 'yyyy-mm-dd'. */
  hasta: string;
};

function commonFields(payload: CampaniaPayload): Partial<CampaniasFields> {
  return {
    NombreCampania_C: payload.nombre.trim(),
    ArticuloID_C: payload.articuloId,
    Articulo_C: payload.articuloNombre,
    Descuento_C: percentToStoredDecimal(payload.descuentoPct),
    Desde_C: isoToAr(payload.desde),
    Hasta_C: isoToAr(payload.hasta),
  };
}

export async function createCampania(payload: CampaniaPayload): Promise<CampaniaItem> {
  const now = new Date();
  return campaniasClient.createItem({
    Title: '[Sumar]',
    Status_C: 'Activo',
    ...commonFields(payload),
    FechaCreacion_C: `${pad2(now.getDate())}/${pad2(now.getMonth() + 1)}/${now.getFullYear()}`,
    MesCreacion_C: pad2(now.getMonth() + 1),
    AnioCreacion_C: String(now.getFullYear()),
    MesAnioCreacion_C: `${pad2(now.getMonth() + 1)}/${now.getFullYear()}`,
    HoraCreacion_C: `${pad2(now.getHours())}:${pad2(now.getMinutes())}`,
  });
}

export async function updateCampania(
  id: string,
  payload: CampaniaPayload,
): Promise<CampaniaItem> {
  return campaniasClient.updateItem(id, commonFields(payload));
}

export async function softDeleteCampania(id: string): Promise<CampaniaItem> {
  return campaniasClient.updateItem(id, { Status_C: 'Inactivo' });
}
